class CartBundle extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.boxVariantId = this.dataset.variantId;
    this.discount = this.dataset.discount;
    this.formId = this.dataset.form;
    this.maxQuantity = Number(this.dataset.max);
    this.textNotification = this.dataset.notification;
    this.maxAmountPerItems = this.dataset.maximumAmount;
    this.isToggleSwithMode = this.dataset.isToggle;
    this.currentQuantity = 0;
    this.caches = {};

    this.onBoxClick = this.onBoxClick.bind(this);
    this.onQuantityBoxClick = this.onQuantityBoxClick.bind(this);
    this.onQuantityButtonClick = this.onQuantityButtonClick.bind(this);
    this.onToggleSwitchChange = this.toggleSwithMode.bind(this);
    this.onPropertyChange = this.onPropertyChange.bind(this);
  }

  get quantity() {
    return this.currentQuantity;
  }

  set quantity(value) {
    this.currentQuantity = value;
    this.toggleInactiveBoxes(this.isMaxQuantityReached());
    this.updateLineProperties();
    this.toggleProductFormDisabled(this.quantity < this.maxQuantity);
  }

  connectedCallback() {
    this.initializeSelectors();
    this.attachProperties();
    this.setupEventListeners();
    this.toggleProductFormDisabled(true);
    if (this.sizeNotification) this.sizeNotification.textContent = this.textNotification;
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(this.reset.bind(this), 2000);
    });
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  initializeSelectors() {
    this.fieldsets = this.querySelectorAll('.cart-bundle__list');
    this.cartLineProperties = this.querySelector('#cart-line-properties');
    this.quantityBoxes = this.querySelectorAll('.bundle-box');
    this.boxesQuantity = this.querySelectorAll('.bundle-box__quantity');
    this.boxesQuantityInput = this.querySelectorAll('.bundle-box__quantity input');
    this.boxesQuantityButton = this.querySelectorAll('.bundle-box__quantity button');
    this.sizeNotification = document.querySelector('.size-notification');
    this.elements = document.querySelector(`#${this.formId}`)?.elements || [];
    this.elementsProperties = [...this.elements].filter((element) => element.name?.includes('properties'));
    this.toggleSwith = document.querySelector('.cart-bundle__toggle input');
    this.templateList = {
      noQuaranteed: this.querySelector('template[data-no-guaranteed]')
        ? [...this.querySelector('template[data-no-guaranteed]').content.cloneNode(true).children]
        : null,
      yesQuaranteed: this.querySelector('template[data-yes-guaranteed]')
        ? [...this.querySelector('template[data-yes-guaranteed]')?.content.cloneNode(true).children]
        : null,
    };

    this.priceSelectors = {
      btn: null,
      price: document.querySelector('.product-info .price-dynamic__money'),
    };
  }

  setupEventListeners() {
    this.quantityBoxes.forEach((box) => box.addEventListener('click', this.onBoxClick));

    this.boxesQuantity.forEach((box) => box.addEventListener('click', this.onQuantityBoxClick));

    this.boxesQuantityButton.forEach((btn) => btn.addEventListener('click', this.onQuantityButtonClick));

    this.toggleSwith?.addEventListener('change', this.onToggleSwitchChange);

    this.elementsProperties.forEach((el) => {
      if (el instanceof HTMLInputElement) {
        el.addEventListener('change', this.onPropertyChange);
      }
    });
  }

  removeEventListeners() {
    this.quantityBoxes.forEach((box) => box.removeEventListener('click', this.onBoxClick));

    this.boxesQuantity.forEach((box) => box.removeEventListener('click', this.onQuantityBoxClick));

    this.boxesQuantityButton.forEach((btn) => btn.removeEventListener('click', this.onQuantityButtonClick));

    this.toggleSwith?.removeEventListener('change', this.onToggleSwitchChange);

    this.elementsProperties.forEach((el) => {
      if (el instanceof HTMLInputElement) {
        el.removeEventListener('change', this.onPropertyChange);
      }
    });
  }

  onBoxClick(event) {
    const box = event.currentTarget;
    this.toggleBox(box);
  }

  onQuantityBoxClick(event) {
    event.stopPropagation();
  }

  onQuantityButtonClick(event) {
    this.changeBoxQuantity(event);
  }

  onPropertyChange() {
    this.updateLineProperties();
  }

  toggleBox(box) {
    const isActive = box.classList.contains('bundle-box--active');
    const input = box.querySelector('.bundle-box__quantity input');
    const inventory = Number(input.dataset.inventory || Infinity);

    if (!isActive && inventory <= 0) return;

    const newQuantity = isActive ? 0 : 1;

    this.updateInputValue(input, newQuantity);
    box.classList.toggle('bundle-box--active', !isActive);

    this.quantity = this.calculateTotalQuantity();
    this.updateButtonsState();
    this.calculateTotalPrice();
    this.shouldDisabledFieldset(box);
  }

  changeBoxQuantity(event) {
    event.stopPropagation();
    const { name } = event.target;
    const box = event.target.closest('.bundle-box');
    const input = box.querySelector('.bundle-box__quantity input');
    const inventory = Number(input.dataset.inventory || Infinity);

    if (name === 'plus') {
      const currentValue = Number(input.value);
      const remainingGlobal = this.maxQuantity - this.quantity;

      if (this.isMaxQuantityReached()) return;
      if (currentValue >= inventory) return;
      if (remainingGlobal <= 0) return;

      input.stepUp();
      box.classList.add('bundle-box--active');
    }

    if (name === 'minus') {
      input.stepDown();
      if (input.value == 0) {
        box.classList.remove('bundle-box--active');
      }
    }

    this.quantity = this.calculateTotalQuantity();
    this.updateButtonsState();
    this.calculateTotalPrice();
    this.shouldDisabledFieldset(box);
  }

  updateInputValue(input, value) {
    if (input) input.value = value;
  }

  updateLineProperties() {
    const time = Date.now().toString();

    const createHiddenInput = ({ id, amount }, formId, index) => {
      const createInput = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('form', formId);
        input.value = value;
        return input;
      };

      const fieldset = document.createElement('fieldset');
      const base = `items[${index + 1}]`;

      fieldset.append(
        createInput(`${base}[id]`, id),
        createInput(`${base}[quantity]`, amount),
        createInput(`${base}[properties][_bundleId]`, time),
        createInput(`${base}[properties][_maidId]`, this.boxVariantId),
        createInput(`${base}[properties][_discount]`, this.discount)
      );

      const allProperties = [];
      this.elementsProperties.forEach(({ name, value }) => {
        const normalizeField = name.match(/properties\[(.*?)\]/);
        if (normalizeField[1] && value) {
          allProperties.push(`${normalizeField[1]}&&${value}`);
        }
      });

      if (allProperties.length) {
        fieldset.append(createInput(`${base}[properties][_pin]`, allProperties.join('&&&')));
      }

      return fieldset;
    };

    const properties = [...this.boxesQuantityInput]
      .filter((input) => Number(input.value) !== 0)
      .map((input) => ({
        id: input.dataset.id,
        amount: input.value,
      }));

    this.cartLineProperties.replaceChildren(
      ...properties.map((obj, index) => createHiddenInput(obj, this.formId, index))
    );
  }

  toggleInactiveBoxes(disabled) {
    this.getInactiveBoxes().forEach((box) => box.classList.toggle('bundle-box--disabled', disabled));
  }

  toggleProductFormDisabled(isDisabled) {
    const productForms = document.querySelectorAll('buy-buttons');
    productForms.forEach((form) => form.classList.toggle('form-disabled', isDisabled));

    this.toggleNotification(isDisabled);
  }

  toggleNotification(show) {
    this.sizeNotification?.classList.toggle('size-notification--show', show);
  }

  updateButtonsState() {
    this.boxesQuantityInput.forEach((input) => {
      const inventory = Number(input.dataset.inventory || Infinity);
      const box = input.closest('.bundle-box');
      const plusBtn = box.querySelector('button[name="plus"]');

      plusBtn.disabled = this.isMaxQuantityReached() || Number(input.value) >= inventory;
    });
  }

  calculateTotalQuantity() {
    return [...this.boxesQuantityInput].reduce((acc, input) => acc + Number(input.value), 0);
  }

  calculateTotalPrice() {
    const { btn, price } = this.priceSelectors;
    const activeInputs = [...this.quantityBoxes]
      .filter((box) => box.classList.contains('bundle-box--active'))
      .map((box) => box.querySelector('input[data-price]'));

    const totalPrice = [...activeInputs].reduce(
      (acc, input) => acc + Number(input.getAttribute('data-price')) * Number(input.value),
      0
    );
    const shopifyFormat = themeVariables.settings.moneyFormatString;
    const shopifyFormatMoney = themeVariables.utils.money;
    const totalPriceMoney = shopifyFormatMoney(totalPrice, shopifyFormat);
    if (btn instanceof HTMLElement) {
      btn.toggleAttribute('hidden', totalPrice === 0);
      btn.textContent = `- ${totalPriceMoney}`;
    }
    if (price instanceof HTMLElement) {
      price.toggleAttribute('hidden', totalPrice === 0);
      price.textContent = `${totalPriceMoney}`;
    }
  }

  getInactiveBoxes() {
    return [...this.quantityBoxes].filter((box) => !box.classList.contains('bundle-box--active'));
  }

  isMaxQuantityReached() {
    return this.quantity >= this.maxQuantity;
  }

  attachProperties() {
    if (this.maxAmountPerItems === 'null') return;
    const amountPerItems = this.maxAmountPerItems.split(',');
    if (Array.isArray(amountPerItems)) {
      amountPerItems.forEach((amount, index) => {
        const currentFieldset = this.fieldsets[index];
        if (currentFieldset && currentFieldset instanceof HTMLDivElement) {
          currentFieldset.setAttribute('data-max-item', amount);
        }
      });

      this.setAttribute('data-amount-mode', 'true');
    }
  }

  shouldDisabledFieldset(currentBox) {
    const isAmountMode = this.dataset.amountMode === 'true';
    if (!isAmountMode || !currentBox) return;

    const currentFieldset = currentBox.closest('.cart-bundle__list');
    const errorNotification = currentFieldset.previousElementSibling;
    const maxItem = Number(currentFieldset.dataset.maxItem);

    const currentAmountInFieldset = [...currentFieldset.querySelectorAll('.bundle-box__quantity input')].reduce(
      (sum, input) => sum + Number(input.value || 0),
      0
    );

    const isDisabled = currentAmountInFieldset >= maxItem;
    currentFieldset.classList.toggle('cart-bundle__list--disabled', isDisabled);

    const typeNotification = maxItem === 1 ? 'single' : 'multi';
    const nameDataAttribute = `data-${typeNotification}-error`;
    const notificationText = errorNotification.getAttribute(nameDataAttribute);

    clearTimeout(errorNotification._hideTimer);
    clearTimeout(errorNotification._showTimer);

    if (isDisabled && notificationText) {
      const text = notificationText.replace('[]', maxItem);

      errorNotification.classList.add('visible');
      errorNotification.textContent = text;

      errorNotification._showTimer = setTimeout(() => {
        errorNotification.style.opacity = '1';
      }, 20);
    } else {
      errorNotification.style.opacity = '0';

      errorNotification._hideTimer = setTimeout(() => {
        errorNotification.textContent = '';
        errorNotification.classList.remove('visible');
      }, 300);
    }
  }

  toggleSwithMode(event) {
    const isChecked = event.target.checked;
    const {
      dataset: { noVariant, yesVariant },
    } = event.target;

    const { noQuaranteed = [], yesQuaranteed = [] } = this.templateList || {};

    const optionContainers = this.querySelectorAll('.cart-bundle__list');
    const currentActiveBoxes = [...optionContainers].map((container) => {
      const boxes = container.querySelectorAll('.bundle-box');
      const index = [...boxes].findIndex((box) => box.classList.contains('bundle-box--active'));

      return {
        position: index !== -1 ? index : 0,
        box: index !== -1 ? boxes[index] : boxes[0],
      };
    });
    console.log(currentActiveBoxes);
    const activeList = isChecked ? yesQuaranteed : noQuaranteed;
    const activeVariantId = isChecked ? yesVariant : noVariant;
    this.boxVariantId = activeVariantId;

    const optionsByProductId = [...activeList].reduce((acc, option) => {
      const productId = option.dataset.productId;
      if (!productId) return acc;

      if (!acc[productId]) {
        acc[productId] = [];
      }

      acc[productId].push(option);
      return acc;
    }, {});

    const groupedOptions = Object.values(optionsByProductId);

    optionContainers.forEach((container, index) => {
      const options = groupedOptions[index];

      if (!options || !options.length) return;

      container.innerHTML = '';
      options.forEach((option) => container.appendChild(option));
    });

    this.reset();

    this.removeEventListeners();
    this.initializeSelectors();
    this.setupEventListeners();
    this.toggleProductFormDisabled(true);
    this.reRenderSection(activeVariantId);
    this.saveOptions(currentActiveBoxes, optionContainers);
    this.updateLineProperties();
    this.quantity = this.calculateTotalQuantity();
  }

  saveOptions(activeBoxes, optionContainers) {
    optionContainers.forEach((container, index) => {
      const activeData = activeBoxes[index];
      if (!activeData) return;

      const { position, box: sourceBox } = activeData;
      const boxes = container.querySelectorAll('.bundle-box');
      const targetBox = boxes[position] || boxes[0];
      if (targetBox.classList.contains('bundle-box--out-stock')) {
        this.shouldDisabledFieldset(boxes[0]);
        return;
      }
      if (!targetBox || !sourceBox) {
        this.shouldDisabledFieldset(targetBox);
        return;
      }
      const targetInput = targetBox.querySelector('input');
      const sourceInput = sourceBox.querySelector('input');
      if (!targetInput || !sourceInput) {
        this.shouldDisabledFieldset(targetBox);
        return;
      }

      targetBox.classList.toggle('bundle-box--active', sourceBox.classList.contains('bundle-box--active'));

      if (targetInput && sourceInput) {
        targetInput.value = sourceInput.value;
      }

      this.shouldDisabledFieldset(targetBox);
    });
  }

  reset() {
    document.querySelectorAll('.bundle-box__quantity input').forEach((input) => (input.value = 0));
    document.querySelectorAll('.bundle-box').forEach((box) => box.classList.remove('bundle-box--active'));
    this.setAttribute('data-is-load', 'true');
    this.currentQuantity = 0;
    this.quantity = 0;
    this.cartLineProperties.innerHTML = '';
  }

  async reRenderSection(id) {
    if (!id || !this.sectionId) return;

    if (this.caches[id]) {
      const parsedDoc = new DOMParser().parseFromString(this.caches[id], 'text/html');
      this.replaceContent(parsedDoc, id);
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('variant', id);

    try {
      const response = await fetch(url.toString());
      const html = await response.text();
      const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
      this.replaceContent(parsedDoc, id);
      this.caches[id] = html;
    } catch (error) {
      console.error(error);
    }
  }

  replaceContent(parsedDoc, id) {
    console.log('parsedDoc', parsedDoc);
    const blockTypes = [
      'sku',
      'badges',
      'price',
      'payment-terms',
      'variant-picker',
      'inventory',
      'buy-buttons',
      'pickup-availability',
      'liquid',
      'klavio',
      'product-style',
      'quantity-selector',
      'subscription-new',
    ];

    blockTypes.forEach((blockType) => {
      this.closest('.shopify-section')
        .querySelectorAll(`[data-block-type="${blockType}"]`)
        .forEach((currentBlock) => {
          const blockId = currentBlock.getAttribute('data-block-id');

          const newBlock =
            parsedDoc.querySelector(`[data-block-type="${blockType}"][data-block-id="${blockId}"]`) ||
            parsedDoc.querySelector(`[data-block-type="${blockType}"]`);

          if (!newBlock) return;

          newBlock.querySelectorAll('noscript').forEach((el) => el.remove());

          if (blockType === 'buy-buttons') {
            if (this.currentQuantity != this.maxQuantity) {
              newBlock.querySelector('buy-buttons')?.classList.add('form-disabled');
            } else {
              newBlock.querySelector('buy-buttons')?.classList.remove('form-disabled');
            }
            newBlock.querySelectorAll('[data-load]').forEach((element) => element.setAttribute('data-load', 'true'));

            currentBlock.replaceWith(newBlock);
          } else {
            currentBlock.replaceWith(newBlock);
          }
        });
    });
  }
}

if (!customElements.get('cart-bundle')) {
  customElements.define('cart-bundle', CartBundle);
}
